import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _excluded = ["input", "value", "onFocus", "onBlur", "onChange"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { assign, noop } from 'lodash';
import React from 'react';
// This can also be used just to flatten the children, but cloning seems the more likely use case.
export function cloneAll(children, cloneProps) {
  var makeClone = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : React.cloneElement;
  var hasKeyPrefix = cloneProps && (cloneProps.key || cloneProps.key === 0);
  var childArray = React.Children.toArray(children);
  return childArray.reduce(function (returnVal, child, index) {
    if (!child || typeof child === 'string' || typeof child === 'number') {
      returnVal.push(child);
    } else {
      var key = hasKeyPrefix ? "".concat(cloneProps === null || cloneProps === void 0 ? void 0 : cloneProps.key).concat(child.key) : child.key;
      var props;

      if (cloneProps) {
        props = _objectSpread(_objectSpread({}, cloneProps), {}, {
          key: key
        });
      } else if (hasKeyPrefix) {
        props = {
          key: key
        };
      }

      if (child.type === React.Fragment) {
        if (child.props.children) {
          returnVal.push.apply(returnVal, _toConsumableArray(cloneAll(child.props.children, props, makeClone)));
        }
      } else {
        returnVal.push(makeClone(child, props, index));
      }
    }

    return returnVal;
  }, []);
}
// Keep in mind that unlike a normal ref, hybrid refs are not guaranteed to be stable.
export function useMergedRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }

  var refFunc = function refFunc(value) {
    if (!value) return;
    hybridRef.current = value;

    for (var _i = 0, _refs = refs; _i < _refs.length; _i++) {
      var ref = _refs[_i];

      if (!ref) {
        continue;
      } else if (typeof ref === 'function') {
        ref(value);
      } else {
        ref.current = value;
      }
    }
  }; // eslint-disable-next-line react-hooks/exhaustive-deps


  var hybridRef = React.useCallback(refFunc, refs);

  if (hybridRef === refFunc) {
    Object.defineProperty(hybridRef, 'current', {
      writable: true,
      enumerable: true
    });
    Object.preventExtensions(hybridRef);
  }

  return hybridRef;
}

var shallowCmp = function shallowCmp(prev, current) {
  if (prev.length !== current.length || !prev.every(function (d, i) {
    return d === current[i];
  })) {
    return current;
  }

  return prev;
}; // Similar to useMemo/useCallback except stability is guaranteed.


export var useValue = function useValue(val, deps) {
  var cmp = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : shallowCmp;
  var ref = React.useRef(SENTINEL);
  var value = ref.current.value;
  var prevDeps = ref.current.dependencies;
  var dependencies = cmp(prevDeps, deps);

  if (dependencies !== prevDeps) {
    value = typeof val === 'function' ? val.apply(void 0, _toConsumableArray(dependencies)) : val;
    ref.current = {
      value: value,
      dependencies: dependencies
    };
  }

  return value;
};
var SENTINEL = {
  dependencies: [Object.create(null)]
};
// An immutable container with mutable contents; basically, a self-updating ref.
export var useBox = function useBox(box, init) {
  var ref = React.useRef();

  if (ref.current === undefined) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
      args[_key2 - 2] = arguments[_key2];
    }

    ref.current = init ? init.apply(void 0, args) : {};
  }

  return assign(ref.current, box);
};
export function useStateWithCallback(initialState) {
  var _React$useState = React.useState(initialState),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      state = _React$useState2[0],
      setState = _React$useState2[1];

  var storedCallback = React.useRef(noop);
  var setStateWithCallback = React.useCallback(function (v) {
    var callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
    storedCallback.current = callback;
    setState(v);
  }, []);
  React.useEffect(function () {
    storedCallback.current();
    storedCallback.current = noop;
  }, [state]);
  return [state, setStateWithCallback];
}

var inc = function inc(x) {
  return x + 1;
};

export var useRenderTrigger = function useRenderTrigger() {
  return React.useReducer(inc, 0)[1];
};

var initControlBox = function initControlBox(trigger) {
  var vars = {
    value: null,
    isFocused: false,
    onChange: noop,
    handleFocus: function handleFocus(e) {
      var _vars$onFocus;

      vars.isFocused = true;
      (_vars$onFocus = vars.onFocus) === null || _vars$onFocus === void 0 ? void 0 : _vars$onFocus.call(vars, e);
    },
    handleBlur: function handleBlur(e) {
      var _vars$onBlur;

      vars.isFocused = false;
      (_vars$onBlur = vars.onBlur) === null || _vars$onBlur === void 0 ? void 0 : _vars$onBlur.call(vars, e);
      trigger(); // Now rerender with the parent's value.
    },
    handleChange: function handleChange(e) {
      vars.value = e.target.value;
      vars.onChange(e);
      trigger();
    }
  };
  return vars;
}; // Use this to make an input "uncontrolled" while focused, but controlled otherwise;
// this is useful when valid values are limited but you don't want to interfere
// with user input by trying to normalize the value as they type.


export var SemiControlled = function SemiControlled(props) {
  var input = props.input,
      controlledValue = props.value,
      onFocus = props.onFocus,
      onBlur = props.onBlur,
      onChange = props.onChange,
      rest = _objectWithoutProperties(props, _excluded);

  var trigger = useRenderTrigger();
  var vars = useBox({
    onFocus: onFocus,
    onBlur: onBlur,
    onChange: onChange,
    controlledValue: controlledValue
  }, initControlBox, trigger);

  if (!vars.isFocused) {
    vars.value = controlledValue;
  }

  return /*#__PURE__*/React.createElement(input, _objectSpread(_objectSpread({}, rest), {}, {
    onFocus: vars.handleFocus,
    onBlur: vars.handleBlur,
    onChange: vars.handleChange,
    value: vars.value
  }));
};